class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.abortController?.abort();
    FacetFiltersForm.abortController = new AbortController();
    const requestId = FacetFiltersForm.requestId = (FacetFiltersForm.requestId || 0) + 1;
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const grid = document.getElementById('ProductGridContainer');
    grid.setAttribute('aria-busy', 'true');
    grid.querySelector('.collection')?.classList.add('loading');
    document.getElementById('FacetFiltersError')?.remove();
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, requestId);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, requestId) {
    return fetch(url, { signal: FacetFiltersForm.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Filter request failed');
        return response.text();
      })
      .then((responseText) => {
        if (requestId !== FacetFiltersForm.requestId) return;
        const html = responseText;
        FacetFiltersForm.cacheSection(url, html);
        FacetFiltersForm.renderSection(html, event);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || requestId !== FacetFiltersForm.requestId) return;
        FacetFiltersForm.finishLoading();
        const message = document.createElement('p');
        message.id = 'FacetFiltersError';
        message.setAttribute('role', 'alert');
        message.append('Results could not be updated. ');
        const retry = document.createElement('a');
        retry.href = window.location.href;
        retry.textContent = 'Reload these results';
        message.append(retry);
        document.getElementById('ProductGridContainer').before(message);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const entry = FacetFiltersForm.filterData.find(filterDataUrl);
    FacetFiltersForm.cacheSection(entry.url, entry.html); // most recently used moves to the end
    FacetFiltersForm.renderSection(entry.html, event);
  }

  /**
   * HFS (general review 2026-10-01): one filter response is parsed once and the parsed document is shared by the
   * filters, grid and count renders (each used to parse the whole section again: three parses per update). Each
   * render keeps its old (html) signature and only parses when it is called without the shared document.
   */
  static renderSection(html, event) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const restoreFocus = FacetFiltersForm.captureFocus();
    FacetFiltersForm.renderFilters(html, event, parsed);
    FacetFiltersForm.renderProductGridContainer(html, parsed);
    FacetFiltersForm.renderProductCount(html, parsed);
    restoreFocus();
    FacetFiltersForm.retrapDrawer();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
  }

  /**
   * HFS (general review 2026-10-01): responses are kept for back/forward and repeated filters, but only the most
   * recently used FacetFiltersForm.cacheLimit of them (the list used to grow by a full section for every filter
   * change for as long as the page stayed open).
   */
  static cacheSection(url, html) {
    const list = FacetFiltersForm.filterData;
    const index = list.findIndex((entry) => entry.url === url);
    if (index > -1) list.splice(index, 1);
    list.push({ html, url });
    while (list.length > FacetFiltersForm.cacheLimit) list.shift();
  }

  static finishLoading() {
    const grid = document.getElementById('ProductGridContainer');
    grid?.removeAttribute('aria-busy');
    grid?.querySelector('.collection')?.classList.remove('loading');
    document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach((el) => el.classList.remove('loading'));
    FacetFiltersForm.toggleActiveFacets(false);
  }

  /**
   * Filter and sort updates replace markup with innerHTML / outerHTML. If the focused control is one of
   * the replaced nodes, the browser drops focus to <body>. Record the focused element now and return a
   * function that puts focus back on its replacement (matched by id, then name + value) once rendering is done.
   * If the control is gone for good (for example a removed filter pill), focus moves to the filter heading.
   */
  static captureFocus() {
    const active = document.activeElement;
    if (!active || active === document.body || active === document.documentElement) return () => {};
    const id = active.id;
    const name = active.getAttribute('name');
    const value = active.getAttribute('value');
    const formId = active.form ? active.form.id : '';
    const wasPill = !!active.closest('.active-facets');
    const drawer = active.closest('menu-drawer');

    return () => {
      if (active.isConnected) return; // untouched by the render, focus is still there

      let target = id ? document.getElementById(id) : null;
      if (!target && name && formId) {
        const form = document.getElementById(formId);
        if (form) {
          target = Array.from(form.elements).find(
            (el) => el.getAttribute('name') === name && (value === null || el.getAttribute('value') === value)
          );
        }
      }
      const candidates = [target];
      if (!target || wasPill) {
        if (drawer?.mainDetailsToggle?.open) {
          // HFS (audit pass 3, 2026-10-01): a control inside an open facet panel (its Clear link) was replaced with
          // the panel; keep the shopper in that panel, on its Back control, before falling back to the drawer's X.
          candidates.push(drawer.querySelector('details[open] > .mobile-facets__submenu > .mobile-facets__close-button'));
          candidates.push(drawer.querySelector('.mobile-facets__close'));
        }
        candidates.push(document.getElementById('verticalTitle'), document.getElementById('ProductCount'));
      }
      for (const candidate of candidates) {
        if (!candidate) continue;
        if (candidate.tabIndex < 0 && !candidate.hasAttribute('tabindex')) candidate.setAttribute('tabindex', '-1');
        candidate.focus({ preventScroll: true });
        if (document.activeElement === candidate) return;
      }
    };
  }

  /**
   * HFS (audit pass 3, 2026-10-01): the mobile drawer's focus trap (global.js trapFocus) holds the panel element
   * that was open when a facet was expanded. A render that replaces that panel's markup (Clear inside the panel,
   * Clear all, a removed pill) leaves the trap pointing at a detached node: Tab is then swallowed and focus can
   * wander out of the drawer. Re-anchor the trap on the live current panel, keeping whatever has focus there.
   */
  static retrapDrawer() {
    const drawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
    if (!drawer || !drawer.mainDetailsToggle?.open || typeof trapFocus !== 'function') return;
    const panel =
      drawer.querySelector('details[open].menu-opening > .mobile-facets__submenu') ||
      drawer.querySelector('[role="dialog"]') ||
      drawer.mainDetailsToggle;
    const active = document.activeElement;
    trapFocus(panel, panel.contains(active) ? active : getFocusableElements(panel)[0] || panel);
  }

  /**
   * Re-apply the role / aria-expanded / aria-controls / click handling that global.js puts on
   * `[id^="Details-"] summary` at load. Summaries replaced by an AJAX render are new nodes and lose it.
   */
  static initSummary(summary) {
    if (summary) initDetailsSummary(summary);
  }

  static renderProductGridContainer(html, parsed) {
    const doc = parsed || new DOMParser().parseFromString(html, 'text/html');
    document.getElementById('ProductGridContainer').innerHTML = doc.getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, parsed) {
   //  if (window.location.pathname.includes('search') ) {
   //    const endlessCollection = new Ajaxinate({
   //      container: '#products-grid',
   //      pagination: '#AjaxinatePagination',
   //    });
   // } else if (window.location.pathname.includes('collections')) {
   //    const endlessCollection = new Ajaxinate({
   //      container: '#product-grid',
   //      pagination: '#AjaxinatePagination',
   //    });
   //  }
    const count = (parsed || new DOMParser().parseFromString(html, 'text/html')).getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    FacetFiltersForm.finishLoading();

    // The grid, filters and count are all up to date. Tell listeners (a11y-fixes.js announces the count in
    // its own live region; custom.js re-runs its variant display). #ProductGridContainer.innerHTML was
    // replaced above, so nothing inside it can be observed reliably; the event is the stable signal.
    document.dispatchEvent(
      new CustomEvent('hfs:facets-updated', {
        detail: { count: (container.textContent || '').replace(/\s+/g, ' ').trim() },
      })
    );
  }

  static renderFilters(html, event, parsed) {
    const parsedHTML = parsed || new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const filterElement = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (!filterElement) return;
      filterElement.innerHTML = element.innerHTML;
      FacetFiltersForm.initSummary(filterElement.querySelector('summary'));
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const current = document.querySelector(selector);
      if (current) current.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const current = document.querySelector(selector);
      if (!source || !current) return;
      if (selector === '.sorting' && current.contains(document.activeElement)) {
        const select = current.querySelector('select');
        if (select) select.value = source.querySelector('select')?.value || select.value;
        return;
      }
      current.innerHTML = source.innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
      FacetFiltersForm.initSummary(target.querySelector('.facets__summary'));
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.target.classList.contains('mobile-facets__checkbox')) {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.cacheLimit = 10;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    // HFS (audit 2026-10-01, B05): an empty field is an unset bound, not zero. Number('') is 0, which was clamped up
    // to the other field's value, so clearing "To" silently turned an open-ended range into an exact price.
    if (input.value.trim() === '') return;
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));
    const before = input.value; // HFS (FBL-019)

    if (value < min) input.value = min;
    if (value > max) input.value = max;
    if (input.value !== before) this.announceAdjustment(min, max); // HFS (FBL-019)
  }

  // HFS (FBL-019, WCAG 3.3.3): say why a typed price changed. Uses the page's filter status region (a11y-fixes.js),
  // else the visually hidden status inside this price range. The auto-submit is not affected.
  announceAdjustment(min, max) {
    const symbol = (this.querySelector('.field-currency')?.textContent || '').trim();
    const region = document.getElementById('a11y-filter-status') || this.querySelector('[data-hfs-price-status]');
    if (!region) return;
    const text = `Adjusted to the available range ${symbol}${min}\u2013${symbol}${max}`;
    region.textContent = '';
    clearTimeout(this.adjustmentTimer);
    this.adjustmentTimer = setTimeout(() => {
      region.textContent = text;
    }, 100);
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keydown', (event) => {
      if (event.key === ' ') event.preventDefault();
    });
    facetLink.addEventListener('keyup', (event) => {
      if (event.key === ' ') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
